// src/App.jsx
import React from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";

import { stripePromise } from "./utils/stripe";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ScratchFeatureProvider } from "./contexts/ScratchFeatureContext";

import Landing from "./components/pages/Landing";
import Login from "./components/pages/Login";
import SignUp from "./components/pages/SignUp";
import Verification from "./components/pages/Verification";
import PasswordReset from "./components/pages/PasswordReset";
import Dashboard from "./components/pages/Dashboard";
import Generator from "./components/pages/Generator";
import Plans from "./components/pages/Plans";
import PicksHistory from "./components/pages/PicksHistory";
import Contact from "./components/pages/Contact";
import Settings from "./components/pages/Settings";
import { TermsOfUse, PrivacyPolicy, RefundPolicy } from "./components/pages/Legal";

import ScratchDashboard from "./components/pages/ScratchDashboard";
import ScratchGameDetails from "./components/pages/ScratchGameDetails";
import ScratchAlerts from "./components/pages/ScratchAlerts";

import ProtectedRoute from "./components/generics/ProtectedRoute";
import ScratchProtectedRoute from "./components/generics/ScratchProtectedRoute";
import ImpersonationBanner from "./components/generics/ImpersonationBanner";

import AdminDashboard from "./components/pages/admin-dash/Admin-Dashboard";
import AdminRoute from "./components/generics/AdminRoute";

/**
 * Top-level app.
 *
 * Wrapping order (outermost first):
 *   ThemeProvider   → light/dark + localStorage persistence
 *     Elements      → Stripe context (so PaymentModal can be used from
 *                     any page without re-wrapping)
 *       Router      → react-router-dom
 *
 * ThemeProvider must wrap Elements because PaymentModal reads the
 * current theme via useTheme() to style the Stripe CardElement iframe.
 *
 * New routes in this drop:
 *   /contact   — contact form (posts to /site/contact-message)
 *   /picks     — full pick history (destination of Dashboard "View all")
 *   /settings  — user settings (password, subscription, support)
 *   /terms     — Terms of Use
 *   /privacy   — Privacy Policy
 *   /refund    — Refund Policy
 */
function App() {
  return (
    <ThemeProvider>
      <ScratchFeatureProvider>
        <div
          className="min-h-screen w-full overflow-x-hidden"
          style={{ background: "var(--bg)", color: "var(--txt)" }}
        >
        <ImpersonationBanner />
        <Elements stripe={stripePromise}>
          <Router>
            <Routes>
              {/* Public */}
              <Route path="/"               element={<Landing />} />
              <Route path="/login"          element={<Login />} />
              <Route path="/sign-up"        element={<SignUp />} />
              <Route path="/verification"   element={<Verification />} />
              <Route path="/reset-password" element={<PasswordReset />} />
              <Route path="/plans"          element={<Plans />} />
              <Route path="/contact"        element={<Contact />} />

              {/* Legal (public) */}
              <Route path="/terms"   element={<TermsOfUse />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund"  element={<RefundPolicy />} />

              {/* Authenticated */}
              <Route
                path="/dashboard"
                element={<ProtectedRoute element={<Dashboard />} />}
              />
              <Route
                path="/generator"
                element={<ProtectedRoute element={<Generator />} />}
              />
              <Route
                path="/picks"
                element={<ProtectedRoute element={<PicksHistory />} />}
              />
              <Route
                path="/settings"
                element={<ProtectedRoute element={<Settings />} />}
              />
              <Route
                path="/scratch"
                element={<ScratchProtectedRoute element={<ScratchDashboard />} />}
              />
              <Route
                path="/scratch/games/:id"
                element={<ScratchProtectedRoute element={<ScratchGameDetails />} />}
              />
              <Route
                path="/scratch/alerts"
                element={<ScratchProtectedRoute element={<ScratchAlerts />} />}
              />

              <Route path="/payment" element={<Navigate to="/plans" replace />} />

              {/* Admin */}
              <Route
                path="/admin"
                element={<AdminRoute element={<AdminDashboard />} />}
              />

              {/* 404 → home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </Elements>
      </div>
      </ScratchFeatureProvider>
    </ThemeProvider>
  );
}

export default App;
